import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import thanq from "../api/thanq";
import useAdminStore from "../store/adminStore";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Grid,
    Table as MuiTable,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Autocomplete,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import PrintIcon from "@mui/icons-material/Print";
import { enqueueSnackbar } from "notistack";
import { useReactToPrint } from "react-to-print";
import Unauthorized from "../components/Unauthorized";
import QRPrint from "../components/QRPrint";

const phoneRegex = /^\+?\d{10,15}$/;

// Helper function to chunk array into groups of specified size
const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
};

const Companies = () => {
    const navigate = useNavigate();
    const isSuperAdmin = useAdminStore((s) => s.isSuperAdmin());
    const [organizationName, setOrganizationName] = useState("");
    const [adminPhones, setAdminPhones] = useState([]);
    const [errors, setErrors] = useState({ organizationName: "", adminPhones: "" });
    const [submitting, setSubmitting] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [editingId, setEditingId] = useState("");
    const [addUsersDialogOpen, setAddUsersDialogOpen] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState("");
    const [userCount, setUserCount] = useState("");
    const [userCountError, setUserCountError] = useState("");
    const [addingUsers, setAddingUsers] = useState(false);
    const [newlyCreatedUsers, setNewlyCreatedUsers] = useState([]);
    const QRListComponent = useRef();

    useEffect(() => {
        // Only fetch companies if user is super admin
        if (!isSuperAdmin) {
            return;
        }

        let isMounted = true;
        const loadCompanies = async () => {
            try {
                const { data } = await thanq.get("/admin/companies");
                if(!isMounted) return;
                if (data) {
                    const companies = data.companies
                    if (companies) {
                        const normalized = companies.map((company) => ({
                            id: company.id || company._id || String(Date.now() + Math.random()),
                            organizationName: company.name || company.name || "",
                            adminPhones: company.admins || [],
                            adminPhone: company.admins || "",
                            tipsDisabled: company.tipDisabled || false,
                        }));
                        setCompanies(normalized);
                    }
                }
            } catch (e) {
                enqueueSnackbar(e.message, { variant: "error" });
            }
        };
        loadCompanies();
        return () => {
            isMounted = false;
        };
    }, [isSuperAdmin]);

    const validate = () => {
        const newErrors = { organizationName: "", adminPhones: "" };
        if (!organizationName.trim()) {
            newErrors.organizationName = "Organization name is required";
        }
        const cleaned = (adminPhones || []).map((p) => (p || "").trim()).filter(Boolean);
        if (cleaned.length === 0) {
            newErrors.adminPhones = "At least one admin phone is required";
        } else if (!cleaned.every((p) => phoneRegex.test(p))) {
            newErrors.adminPhones = "Each phone must be 10-15 digits, may start with +";
        }
        setErrors(newErrors);
        return !newErrors.organizationName && !newErrors.adminPhones;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        try {
            setSubmitting(true);
            if (editingId) {
                const cleanedPhones = (adminPhones || []).map((p) => p.trim()).filter(Boolean);
                const payload = {
                    name: organizationName.trim(),
                    admins: cleanedPhones,
                };
                try {
                    const { data: updated } = await thanq.put(`/admin/companies/${editingId}`, payload);
                    const updatedCompany = {
                        id: editingId,
                        organizationName: updated?.name || organizationName.trim(),
                        adminPhones:
                            (Array.isArray(updated?.adminPhones) && updated.adminPhones) ||
                            (Array.isArray(updated?.admins) && updated.admins) ||
                            cleanedPhones,
                        adminPhone:
                            (Array.isArray(updated?.adminPhones) && updated.adminPhones[0]) ||
                            (Array.isArray(updated?.admins) && updated.admins[0]) ||
                            (cleanedPhones[0] || ""),
                        tipDisabled: updated?.tipDisabled ?? false,
                    };
                    setCompanies((prev) => prev.map((c) => (c.id === editingId ? updatedCompany : c)));
                    enqueueSnackbar("Company updated", { variant: "success" });
                } catch (e) {
                    enqueueSnackbar(e.message, { variant: "error" });
                    return; 
                }
            } else {
                const payload = {
                    name: organizationName.trim(),
                    admins: (adminPhones || []).map((p) => p.trim()).filter(Boolean),
                };
                const { data: created } = await thanq.post("/admin/companies", payload);
                const newCompany = {
                    id: created?.id || created?._id || Date.now().toString(),
                    organizationName: created?.name || created?.name || organizationName.trim(),
                    adminPhones:
                        (Array.isArray(created?.adminPhones) && created.adminPhones) ||
                        (Array.isArray(created?.admins) && created.admins) ||
                        (adminPhones || []).map((p) => p.trim()).filter(Boolean),
                    adminPhone:
                        (Array.isArray(created?.adminPhones) && created.adminPhones[0]) ||
                        (Array.isArray(created?.admins) && created.admins[0]) ||
                        ((adminPhones || []).map((p) => p.trim()).filter(Boolean)[0]) || "",
                    tipDisabled: created?.tipDisabled || false,
                };
                setCompanies((prev) => [newCompany, ...prev]);
                enqueueSnackbar("Company created", { variant: "success" });
            }
            setOrganizationName("");
            setAdminPhones([]);
            setEditingId("");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (company) => {
        setEditingId(company.id);
        setOrganizationName(company.organizationName);
        const phones = Array.isArray(company.adminPhones)
            ? company.adminPhones
            : company.adminPhone
                ? [company.adminPhone]
                : [];
        setAdminPhones(phones);
    };

    const handleDelete = async (id) => {
        try {
            await thanq.delete(`/admin/companies/${id}`);
            setCompanies((prev) => prev.filter((c) => c.id !== id));
            if (editingId === id) {
                setEditingId("");
                setOrganizationName("");
                setAdminPhones([]);
            }
            enqueueSnackbar("Company deleted", { variant: "success" });
        } catch (e) {
            enqueueSnackbar(e.message, { variant: "error" });
        }
    };

    const handleToggleTips = async (company) => {
        const newTipDisabled = !company.tipDisabled;
        try {
            await thanq.put(`/admin/companies/${company.id}`, {
                tipDisabled: newTipDisabled,
            });
            setCompanies((prev) =>
                prev.map((c) =>
                    c.id === company.id ? { ...c, tipDisabled: newTipDisabled } : c
                )
            );
            enqueueSnackbar(`Tips ${newTipDisabled ? "disabled" : "enabled"} for ${company.organizationName}`, {
                variant: "success",
            });
        } catch (e) {
            enqueueSnackbar(e.message, { variant: "error" });
        }
    };

    const handleOpenAddUsersDialog = (companyId) => {
        setSelectedCompanyId(companyId);
        setUserCount("");
        setUserCountError("");
        setAddUsersDialogOpen(true);
    };

    const handleCloseAddUsersDialog = () => {
        setAddUsersDialogOpen(false);
        setSelectedCompanyId("");
        setUserCount("");
        setUserCountError("");
        setNewlyCreatedUsers([]);
    };

    const handleAddUsersToCompany = async () => {
        const numValue = parseInt(userCount, 10);
        if (userCount === "" || isNaN(numValue) || numValue <= 0) {
            setUserCountError("Count must be greater than 0");
            return;
        }
        setUserCountError("");
        try {
            setAddingUsers(true);
            const createdUserIds = [];
            for (let i = 0; i < numValue; i++) {
                const { data } = await thanq.post("/admin/user/", { companyId: selectedCompanyId });
                // Extract user ID from response
                const userId = data?.user?.id || data?.user?._id || data?.id || data?._id;
                if (userId) {
                    createdUserIds.push(userId);
                }
            }
            
            // Fetch full user data including profileQR
            if (createdUserIds.length > 0) {
                const { data: usersData } = await thanq.get("/admin/users");
                const allUsers = usersData?.users || [];
                const newlyCreated = allUsers.filter((user) => 
                    createdUserIds.includes(user.id || user._id)
                );
                setNewlyCreatedUsers(newlyCreated);
            }
            
            enqueueSnackbar(`Added ${numValue} new users to company`, { variant: "success" });
        } catch (e) {
            enqueueSnackbar(e.message, { variant: "error" });
        } finally {
            setAddingUsers(false);
        }
    };

    const handleViewUsers = (companyId) => {
        navigate(`/users?companyId=${companyId}`);
    };

    // Split newly created users into chunks of 4 for printing
    const printPages = chunkArray(newlyCreatedUsers, 4);

    const handleProfilesPrint = useReactToPrint({
        contentRef: QRListComponent,
        documentTitle: "ThanQ Profile QRs",
        pageStyle: `
            @page {
                size: A4;
                margin: 0 !important;
            }
            @media print {
                * {
                    -webkit-print-color-adjust: exact !important;
                    color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                body {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .print-page {
                    page-break-after: always;
                    page-break-inside: avoid;
                    height: 100vh;
                    width: 100vw;
                    display: flex !important;
                    flex-direction: row;
                    flex-wrap: wrap;
                    justify-content: space-around;
                    align-items: center;
                    padding: 20px;
                    box-sizing: border-box;
                    margin: 0;
                }
                .print-page:last-child {
                    page-break-after: auto;
                }
                .qr-item {
                    width: 40% !important;
                    height: 40% !important;
                    margin: 20px !important;
                    display: flex !important;
                    justify-content: center !important;
                    align-items: center !important;
                    box-sizing: border-box !important;
                    page-break-inside: avoid !important;
                }
            }
        `,
    });

    // Show unauthorized message if not super admin
    if (!isSuperAdmin) {
        return <Unauthorized />;
    }

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 2 }}>
                Companies
            </Typography>
            <Paper sx={{ p: 3, maxWidth: 600, mb: 3 }}>
                <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Organization Name"
                                value={organizationName}
                                onChange={(e) => setOrganizationName(e.target.value)}
                                onBlur={validate}
                                error={Boolean(errors.organizationName)}
                                helperText={errors.organizationName}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={adminPhones}
                                    onChange={(_, newValue) => {
                                        const cleaned = (newValue || [])
                                            .map((v) => (typeof v === "string" ? v : String(v)))
                                            .map((v) => v.replace(/[^\d+]/g, "").trim())
                                            .filter(Boolean);
                                        const unique = Array.from(new Set(cleaned));
                                        setAdminPhones(unique);
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip variant="outlined"
                                                onClick={() => { }}
                                                label={option}
                                                key={index}
                                                {...getTagProps({ index })} />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Admin Phone Number(s)"
                                            placeholder="Type a phone and press Enter"
                                            onBlur={validate}
                                            error={Boolean(errors.adminPhones)}
                                            helperText={errors.adminPhones || "Digits only, may start with +"}
                                            fullWidth
                                            required
                                            inputProps={{ ...params.inputProps, inputMode: "tel" }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Button type="submit" variant="contained" disabled={submitting}>
                                {editingId ? "Update Company" : "Create Company"}
                            </Button>
                            {editingId && (
                                <Button
                                    sx={{ ml: 2 }}
                                    variant="text"
                                    color="inherit"
                                    onClick={() => {
                                        setEditingId("");
                                        setOrganizationName("");
                                        setAdminPhones([]);
                                    }}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Companies
                </Typography>
                <MuiTable size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Organization</TableCell>
                            <TableCell>Admin Phone(s)</TableCell>
                            <TableCell align="center">Tips</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {companies.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography color="text.secondary">No companies yet.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            companies.map((c) => (
                                <TableRow key={c.id} hover>
                                    <TableCell>{c.organizationName}</TableCell>
                                    <TableCell>
                                        {Array.isArray(c.adminPhones) && c.adminPhones.length > 0
                                            ? c.adminPhones.join(", ")
                                            : c.adminPhone || ""}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            checked={!c.tipDisabled}
                                            onChange={() => handleToggleTips(c)}
                                            color="primary"
                                            size="small"
                                            title={c.tipDisabled ? "Enable Tips" : "Disable Tips"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton aria-label="view users" onClick={() => handleViewUsers(c.id)} size="small" color="info" title="View Users">
                                            <PeopleIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton aria-label="add users" onClick={() => handleOpenAddUsersDialog(c.id)} size="small" color="primary" title="Add Users">
                                            <PersonAddIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton aria-label="edit" onClick={() => handleEdit(c)} size="small" title="Edit">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton aria-label="delete" onClick={() => handleDelete(c.id)} size="small" color="error" title="Delete">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </MuiTable>
            </Paper>
            <Dialog 
                open={addUsersDialogOpen} 
                onClose={handleCloseAddUsersDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {newlyCreatedUsers.length > 0 ? "Newly Created Users" : "Add Users to Company"}
                </DialogTitle>
                <DialogContent>
                    {newlyCreatedUsers.length > 0 ? (
                        <Box>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Successfully created {newlyCreatedUsers.length} user(s). You can print their QR codes below.
                            </Typography>
                            <Box sx={{ maxHeight: 400, overflowY: "auto", mb: 2 }}>
                                <MuiTable size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Handle ID</TableCell>
                                            <TableCell>Phone Number</TableCell>
                                            <TableCell>Name</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {newlyCreatedUsers.map((user, idx) => (
                                            <TableRow key={user.id || user._id || idx}>
                                                <TableCell>{user.handleId || "N/A"}</TableCell>
                                                <TableCell>
                                                    {(user.phoneNumber || "-").replace("+91", "")}
                                                </TableCell>
                                                <TableCell>{user.name || "N/A"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </MuiTable>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PrintIcon />}
                                    onClick={handleProfilesPrint}
                                >
                                    Print QR Codes
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <TextField
                            label="Number of Users"
                            type="number"
                            variant="outlined"
                            fullWidth
                            value={userCount}
                            onChange={(e) => {
                                setUserCount(e.target.value);
                                if (userCountError) {
                                    setUserCountError("");
                                }
                            }}
                            error={!!userCountError}
                            helperText={userCountError}
                            sx={{ mt: 2 }}
                            disabled={addingUsers}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddUsersDialog} color="inherit">
                        {newlyCreatedUsers.length > 0 ? "Close" : "Cancel"}
                    </Button>
                    {newlyCreatedUsers.length === 0 && (
                        <Button onClick={handleAddUsersToCompany} variant="contained" disabled={addingUsers}>
                            {addingUsers ? "Adding..." : "Add Users"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <div style={{ display: "none" }}>
                <div ref={QRListComponent}>
                    {printPages.map((page, pageIndex) => (
                        <div key={pageIndex} className="print-page">
                            {page.map((user, idx) => (
                                <div key={user.id || user._id || idx} className="qr-item">
                                    <QRPrint
                                        handleId={user.handleId}
                                        phone={user.phoneNumber}
                                        name={user.name}
                                        image={user.profileQR}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </Box>
    );
};

export default Companies;


